import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import {
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight,
} from "react-icons/md";
import { FiArrowRight, FiPause, FiPlay } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import ClientCalling from "../../api/ClientCalling";
import SmartImage from "./SmartImage";

const AUTOPLAY = 6000;
const DRAG_THRESHOLD = 80;

const Skeleton = () => (
    <div className='w-full my-6 md:my-10 px-3 sm:px-4'>
        <div className='mx-auto w-full max-w-7xl'>
            <div className='relative w-full aspect-[16/9] md:aspect-[21/9]'>
                <div className='absolute inset-0 rounded-3xl ios-shimmer' />
            </div>
            <div className='mt-6 flex items-center justify-center gap-3'>
                <div className='w-9 h-9 rounded-full ios-shimmer' />
                <div className='flex gap-2'>
                    {[...Array(4)].map((_, i) => (
                        <div
                            key={i}
                            className={`h-1.5 rounded-full ios-shimmer ${
                                i === 0 ? "w-7" : "w-2"
                            }`}
                        />
                    ))}
                </div>
                <div className='w-9 h-9 rounded-full ios-shimmer' />
            </div>
        </div>
    </div>
);

const ClientBanner = () => {
    const [bannerData, setBannerData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const navigate = useNavigate();
    const dragging = useRef(false);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const res = await ClientCalling?.getBannerClient();
                setBannerData(res?.status ? res.data || [] : []);
            } catch (e) {
                console.error("Banner fetch failed:", e);
                setBannerData([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (paused || hovered || bannerData.length <= 1) return;
        const id = setInterval(
            () => setCurrent((c) => (c + 1) % bannerData.length),
            AUTOPLAY
        );
        return () => clearInterval(id);
    }, [paused, hovered, bannerData.length, current]);

    useEffect(() => {
        const onKey = (e) => {
            if (bannerData.length <= 1) return;
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bannerData.length]);

    const next = () => setCurrent((c) => (c + 1) % bannerData.length);
    const prev = () =>
        setCurrent((c) => (c === 0 ? bannerData.length - 1 : c - 1));
    const goTo = (i) => setCurrent(i);

    const handleClick = async (item) => {
        if (dragging.current) return;
        try {
            await ClientCalling?.getBannerClickClient(item._id);
        } catch (e) {
            console.error("Click track failed:", e);
        }
        if (!item) return;
        if (item.b_type === "url" && item.b_url) window.open(item.b_url, "_blank");
        else if (item.b_type === "list" && item.b_list)
            navigate(`/list/${item.b_list}`);
        else navigate(`/category/${item.b_category}`);
    };

    if (loading) return <Skeleton />;
    if (!bannerData || bannerData.length === 0) return null;

    const length = bannerData.length;
    const activeItem = bannerData[current];

    // distance: -2, -1, 0, 1, 2 from current — for the side-peek positions
    const getDistance = (idx) => {
        let d = idx - current;
        if (d > length / 2) d -= length;
        if (d < -length / 2) d += length;
        return d;
    };

    return (
        <section
            className='w-full my-6 md:my-10'
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {/* AMBIENT bg using active image */}
            <div className='relative overflow-hidden'>
                {activeItem?.b_image && (
                    <AnimatePresence mode='wait'>
                        <motion.div
                            key={`bg-${current}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.45 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.8 }}
                            className='absolute inset-0 pointer-events-none'
                        >
                            <img
                                src={activeItem.b_image}
                                alt=''
                                className='absolute inset-0 w-full h-full object-cover scale-110'
                                style={{
                                    filter: "blur(80px) saturate(1.3)",
                                }}
                            />
                            <div
                                className='absolute inset-0'
                                style={{
                                    background:
                                        "linear-gradient(to bottom, rgba(10,8,16,0.4), rgba(10,8,16,0.7) 60%, rgba(10,8,16,1))",
                                }}
                            />
                        </motion.div>
                    </AnimatePresence>
                )}

                <div className='relative mx-auto w-full max-w-4xl px-3 sm:px-4 md:px-6 py-6 md:py-10'>
                    {/* Stage — narrower on desktop */}
                    <div
                        className='relative w-full mx-auto md:max-w-2xl lg:max-w-3xl'
                        style={{
                            perspective: "1400px",
                        }}
                    >
                        <div className='relative w-full aspect-[16/9] md:aspect-[18/10]'>
                            {bannerData.map((item, idx) => {
                                const d = getDistance(idx);
                                const isCenter = d === 0;
                                const abs = Math.abs(d);
                                if (abs > 2) {
                                    // not visible — keep mounted but invisible to avoid layout jump
                                    return (
                                        <motion.div
                                            key={item._id || idx}
                                            className='absolute inset-0 pointer-events-none'
                                            style={{ opacity: 0 }}
                                        />
                                    );
                                }

                                // Position math
                                const x =
                                    d === 0
                                        ? "0%"
                                        : d < 0
                                            ? `${d * 62}%`
                                            : `${d * 62}%`;
                                const scale =
                                    abs === 0 ? 1 : abs === 1 ? 0.82 : 0.68;
                                const opacity =
                                    abs === 0 ? 1 : abs === 1 ? 0.55 : 0.25;
                                const blur = abs === 0 ? 0 : abs === 1 ? 1 : 2;
                                const z = 50 - abs * 10;
                                const rotY =
                                    abs === 0 ? 0 : d < 0 ? 12 : -12;

                                return (
                                    <motion.div
                                        key={item._id || idx}
                                        initial={false}
                                        animate={{
                                            x,
                                            scale,
                                            opacity,
                                            rotateY: rotY,
                                            filter: `blur(${blur}px) brightness(${
                                                abs === 0 ? 1 : 0.7
                                            })`,
                                        }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 220,
                                            damping: 32,
                                            mass: 0.9,
                                        }}
                                        drag={isCenter ? "x" : false}
                                        dragConstraints={{ left: 0, right: 0 }}
                                        dragElastic={0.18}
                                        dragMomentum={false}
                                        onDragStart={() => {
                                            dragging.current = true;
                                        }}
                                        onDragEnd={(e, info) => {
                                            const dx = info.offset.x;
                                            const v = info.velocity.x;
                                            if (dx < -DRAG_THRESHOLD || v < -500) next();
                                            else if (
                                                dx > DRAG_THRESHOLD ||
                                                v > 500
                                            )
                                                prev();
                                            setTimeout(() => {
                                                dragging.current = false;
                                            }, 150);
                                        }}
                                        whileDrag={{ cursor: "grabbing" }}
                                        onClick={
                                            isCenter
                                                ? () => handleClick(item)
                                                : () => goTo(idx)
                                        }
                                        className='absolute inset-0 rounded-3xl overflow-hidden'
                                        style={{
                                            zIndex: z,
                                            cursor: isCenter ? "grab" : "pointer",
                                            transformStyle: "preserve-3d",
                                            transformOrigin: "center center",
                                            willChange:
                                                "transform, opacity, filter",
                                            boxShadow: isCenter
                                                ? "0 40px 80px -20px rgba(127,0,255,0.55), 0 18px 36px -12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.16)"
                                                : "0 20px 40px -16px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)",
                                            border:
                                                "1px solid rgba(255,255,255,0.12)",
                                            background: "rgba(18,18,18,0.5)",
                                        }}
                                    >
                                        {/* Blurred bg fill */}
                                        <img
                                            src={item.b_image}
                                            alt=''
                                            aria-hidden
                                            draggable={false}
                                            className='absolute inset-0 w-full h-full object-cover pointer-events-none scale-110'
                                            style={{
                                                filter:
                                                    "blur(34px) saturate(1.3)",
                                                opacity: 0.5,
                                            }}
                                        />
                                        {/* Sharp foreground */}
                                        <SmartImage
                                            src={item.b_image}
                                            alt={item.b_alt || "banner"}
                                            eager={isCenter}
                                            className='relative w-full h-full object-contain select-none pointer-events-none drop-shadow-2xl'
                                        />
                                        {/* Glass ring */}
                                        <div
                                            className='absolute inset-0 pointer-events-none rounded-3xl'
                                            style={{
                                                boxShadow:
                                                    "inset 0 0 0 1px rgba(255,255,255,0.14)",
                                            }}
                                        />
                                        {/* Bottom info — only on center, only on md+ */}
                                        {isCenter && (item.b_title || item.b_subtitle) && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 16 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.15 }}
                                                className='absolute left-4 right-4 bottom-4 md:left-7 md:bottom-6 md:right-auto md:max-w-xl flex flex-col items-start gap-2 pointer-events-none'
                                            >
                                                {item.b_eyebrow && (
                                                    <div className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-xs text-white font-semibold uppercase tracking-wider'
                                                        style={{
                                                            background:
                                                                "linear-gradient(135deg, rgba(127,0,255,0.5), rgba(255,138,255,0.4))",
                                                            backdropFilter: "blur(12px)",
                                                            border: "1px solid rgba(255,255,255,0.2)",
                                                        }}>
                                                        <HiSparkles
                                                            className='text-xs'
                                                            style={{ color: "#FCFA02" }}
                                                        />
                                                        {item.b_eyebrow}
                                                    </div>
                                                )}
                                                {item.b_title && (
                                                    <h2
                                                        className='text-white font-bold text-lg md:text-3xl leading-tight tracking-tight'
                                                        style={{
                                                            textShadow:
                                                                "0 2px 12px rgba(0,0,0,0.6)",
                                                        }}
                                                    >
                                                        {item.b_title}
                                                    </h2>
                                                )}
                                                {item.b_subtitle && (
                                                    <p
                                                        className='text-white/85 text-xs md:text-sm max-w-md'
                                                        style={{
                                                            textShadow:
                                                                "0 1px 6px rgba(0,0,0,0.7)",
                                                        }}
                                                    >
                                                        {item.b_subtitle}
                                                    </p>
                                                )}
                                            </motion.div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Bottom controls bar */}
                    {length > 1 && (
                        <div className='mt-5 md:mt-7 flex items-center justify-center gap-3'>
                            <motion.button
                                onClick={prev}
                                aria-label='Previous'
                                whileHover={{ scale: 1.08, x: -2 }}
                                whileTap={{ scale: 0.9 }}
                                className='ios-glass-btn w-10 h-10 rounded-full flex items-center justify-center text-white'
                            >
                                <MdKeyboardArrowLeft className='text-xl' />
                            </motion.button>

                            <div
                                className='ios-glass-dark inline-flex items-center gap-1.5 px-3 py-2 rounded-full'
                            >
                                {bannerData.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => goTo(i)}
                                        aria-label={`Slide ${i + 1}`}
                                        className='py-1'
                                    >
                                        <motion.span
                                            className='block h-1.5 rounded-full'
                                            animate={{
                                                width: i === current ? 22 : 6,
                                                opacity: i === current ? 1 : 0.4,
                                            }}
                                            style={{
                                                background:
                                                    i === current
                                                        ? "linear-gradient(90deg, #7F00FF, #FF8AFF)"
                                                        : "#FFFFFF",
                                                boxShadow:
                                                    i === current
                                                        ? "0 0 10px rgba(127,0,255,0.6)"
                                                        : "none",
                                            }}
                                            transition={{
                                                type: "spring",
                                                stiffness: 280,
                                                damping: 26,
                                            }}
                                        />
                                    </button>
                                ))}
                            </div>

                            <motion.button
                                onClick={next}
                                aria-label='Next'
                                whileHover={{ scale: 1.08, x: 2 }}
                                whileTap={{ scale: 0.9 }}
                                className='ios-glass-btn w-10 h-10 rounded-full flex items-center justify-center text-white'
                            >
                                <MdKeyboardArrowRight className='text-xl' />
                            </motion.button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ClientBanner;
